/**
 * D3/PR-3 — round-recap zone settlement row.
 *
 * The recap's zone area is 2-row:
 *   ① in-game <ZoneBoard> read-only (this round's coin placements)
 *   ② <ZoneSettlementRow> (this component) — per-zone 1st-place token + move,
 *      "동점 이동 없음", or "처리 안 함" for zones skipped after a finisher,
 *      with emphasis (active glow) + rise badge animation driven by the
 *      parent timeline's `zoneStates`.
 *
 * Columns align with the ZoneBoard above (3 columns, equal width, same gap),
 * so winners line up beneath their placements.
 *
 * Design contract: docs/wireframes/horror-race/states/round-recap.html `.settle-row`.
 */

import type { RoundResolutionStep, Zone } from "@cloudnest/horror-race-engine";
import { CHARACTER_COLORS, CHARACTER_LABELS } from "./character-display";
import { HorrorCharacterIcon } from "./HorrorCharacterIcon";

export type ZoneSettlementState = "pending" | "active" | "done";

const ZONES: readonly Zone[] = [1, 2, 4];

interface ZoneSettlementRowProps {
  readonly steps: readonly RoundResolutionStep[];
  readonly zoneStates: Readonly<Record<Zone, ZoneSettlementState>>;
}

export function ZoneSettlementRow({
  steps,
  zoneStates,
}: ZoneSettlementRowProps) {
  const stepByZone = new Map(steps.map((s) => [s.zone, s]));
  return (
    <div
      data-testid="round-recap-settlement"
      className="grid grid-cols-3 gap-2"
    >
      {ZONES.map((zone) => (
        <ZoneSettlementCell
          key={zone}
          zone={zone}
          state={zoneStates[zone]}
          step={stepByZone.get(zone)}
        />
      ))}
    </div>
  );
}

interface ZoneSettlementCellProps {
  readonly zone: Zone;
  readonly state: ZoneSettlementState;
  readonly step: RoundResolutionStep | undefined;
}

function ZoneSettlementCell({ zone, state, step }: ZoneSettlementCellProps) {
  const winner = step?.winner ?? null;
  const skipped = step === undefined;
  const hasWinner = winner !== null;
  return (
    <div
      data-testid="round-recap-zone"
      data-zone={zone}
      data-state={state}
      data-winner={winner ?? ""}
      data-skipped={skipped ? "true" : undefined}
      className="relative flex min-h-[58px] flex-col items-center justify-center gap-0.5 rounded-xl p-2 text-center"
      style={{
        backgroundColor: "var(--cn-horror-track)",
        border:
          state === "active"
            ? "1px solid var(--cn-horror-finished-flash)"
            : "1px solid var(--cn-horror-surface-border)",
        opacity: state === "pending" ? 0.45 : state === "done" ? 0.9 : 1,
        boxShadow:
          state === "active" ? "0 0 12px rgba(255,215,0,0.35)" : "none",
        transition:
          "opacity .3s ease, box-shadow .3s ease, border-color .3s ease",
      }}
    >
      {/* ↑ +N rise badge — appears only on active winner cells (no-op on ties). */}
      {state === "active" && hasWinner && (
        <span
          data-testid="round-recap-zone-rise"
          aria-hidden="true"
          className="hr-zone-rise pointer-events-none absolute font-extrabold"
          style={{
            top: 2,
            right: 6,
            fontSize: 9,
            color: "var(--cn-horror-finished-flash)",
          }}
        >
          ↑ +{zone}
        </span>
      )}
      <div
        className="font-extrabold font-mono text-[8.5px]"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        +{zone} 구역 1등
      </div>
      {hasWinner ? (
        <>
          <span
            className={state === "active" ? "hr-zone-token-pop" : undefined}
          >
            <HorrorCharacterIcon
              character={winner}
              size={26}
              color={CHARACTER_COLORS[winner]}
            />
          </span>
          <span
            className="font-extrabold text-[11px]"
            style={{ color: CHARACTER_COLORS[winner] }}
          >
            {CHARACTER_LABELS[winner]} +{zone}
          </span>
        </>
      ) : skipped ? (
        <span
          className="text-[10.5px] leading-tight"
          style={{ color: "var(--cn-horror-text-muted)" }}
        >
          레이스 종료
          <br />
          처리 안 함
        </span>
      ) : (
        <span
          className="text-[10.5px] leading-tight"
          style={{ color: "var(--cn-horror-text-muted)" }}
        >
          동점
          <br />
          이동 없음
        </span>
      )}
    </div>
  );
}
